import { Dialog, DialogPanel } from "@headlessui/react";
import {
  Bell,
  People,
  BookmarkStar,
  CreditCard,
  EnvelopeOpen,
  FileEarmarkText,
  ChatLeftText,
  ChevronLeft,
} from "react-bootstrap-icons";
import {
  NavItem,
  PageHeader,
  PageTitle,
  useNavigationFilter,
} from "@components";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import {
  Notifications,
  Team,
  Roles,
  General,
  SharedMailboxes,
  TemplatesNew,
  PaymentProfiles,
  CashSales,
  Ecommerce,
  BankAccounts,
  Terminals,
  QueriesSettings,
} from ".";
import { useMerchant, updateMerchant, updateUser, readCustomer } from "@auth";
import { useRequestTemplates, useQueries, usePortalNotifications } from "@flags";
import NotificationToaster from "../../portal-template/components/NotificationToaster";
import { ecommerceModule, customersModule } from "@auth/modules";
import usePaymentSettingsForm from "./hooks/usePaymentSettingsForm";

const BASE = "/settings";

export const SettingsTemplate = () => {
  const { hasPermission } = useMerchant();
  const requestTemplatesEnabled = useRequestTemplates();
  const queriesEnabled = useQueries();
  const portalNotificationsEnabled = usePortalNotifications();
  const { filterNavigation } = useNavigationFilter();
   const { hasModule } = useMerchant();
  const hasEcommerceModule = hasModule(ecommerceModule);
  const {
      paymentSettings,
    } = usePaymentSettingsForm();
  const isAdyen = paymentSettings?.cardPaymentProvider === "Adyen";


  const navGroups = [
    {
      name: "Account",
      navItems: [
        {
          name: "Notifications",
          to: `${BASE}/notifications`,
          path: "notifications",
          page: <Notifications />,
          icon: Bell,
        },
      ],
    },
    {
      name: "Workspace",
      navItems: [
        hasPermission(updateUser) && {
          name: "Team",
          to: `${BASE}/team`,
          path: "team",
          page: <Team />,
          icon: People,
        },
        hasPermission(updateUser) && {
          name: "Roles",
          to: `${BASE}/roles`,
          path: "roles",
          page: <Roles />,
          icon: BookmarkStar,
        },
        hasPermission(updateMerchant) && {
          name: "Payments",
          icon: CreditCard,
          permission: updateMerchant,
          children: [
            {
              name: "Cash sales",
              to: `${BASE}/cash-sales`,
              path: "cash-sales",
              page: <CashSales />,
            },
            hasPermission(readCustomer) && {
              name: "Trade accounts",
              to: `${BASE}/trade-accounts`,
              path: "trade-accounts",
              page: <PaymentProfiles />,
            },
            hasEcommerceModule && {
              name: "E-commerce",
              to: `${BASE}/e-commerce`,
              path: "e-commerce",
              page: <Ecommerce />,
            },
            isAdyen &&
              paymentSettings?.terminals?.enabled && {
                name: "Terminals",
                to: `${BASE}/terminals`,
                path: "terminals",
                page: <Terminals />,
              },
            isAdyen && {
              name: "General",
              to: `${BASE}/payments`,
              path: "payments",
              page: <General />,
            },
            {
              name: "Bank accounts",
              to: `${BASE}/bank-accounts`,
              path: "bank-accounts",
              page: <BankAccounts />,
            },
          ].filter(Boolean),
        },
        hasPermission(updateMerchant) && {
          name: "Shared mailboxes",
          to: `${BASE}/shared-mailboxes`,
          path: "shared-mailboxes",
          page: <SharedMailboxes />,
          icon: EnvelopeOpen,
        },
        queriesEnabled && hasModule(customersModule) && hasPermission(updateMerchant) && {
          name: "Query reasons",
          to: `${BASE}/queries`,
          path: "queries",
          page: <QueriesSettings />,
          icon: ChatLeftText,
        },
        requestTemplatesEnabled && {
          name: "Templates",
          to: `${BASE}/templates`,
          path: "templates",
          page: <TemplatesNew />,
          icon: FileEarmarkText,
        },
      ].filter(
        (item) => item && (!item.children || item.children.length > 0),
      ),
    },
  ];

  const flattenRoutes = (items) =>
    items.flatMap((item) => {
      const self = item.path ? [item] : [];
      return item.children ? [...self, ...flattenRoutes(item.children)] : self;
    });

  const allRoutes = navGroups.flatMap((g) => flattenRoutes(g.navItems));

  const onClose = () => {};

  const sideNav = (
    <div className="flex grow flex-col overflow-y-auto border-r border-zinc-200 bg-zinc-50">
      <NavLink to="..">
        <div className="flex h-12 shrink-0 gap-x-2 items-center font-semibold border-b border-zinc-200 px-3 hover:bg-zinc-100">
          <ChevronLeft />
          Settings
        </div>
      </NavLink>
      <nav className="flex flex-1 flex-col">
        {navGroups.map((group) =>
          group.navItems.length > 0 ? (
            <div key={group.name} className="pt-3 px-2">
              <div className="text-zinc-500 text-xs px-2 pb-[1px]">
                {group.name}
              </div>
              <ul className="space-y-px">
                {filterNavigation(group.navItems).map((item) => (
                  <li key={item.name}>
                    <NavItem item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null,
        )}
      </nav>
    </div>
  );

  return (
    <Dialog open onClose={onClose} className="relative z-[35]">
      <div className="pointer-events-none fixed inset-0 flex w-screen">
        <DialogPanel className="pointer-events-auto w-full">
          <div className="h-full w-full flex">
            <div className="hidden lg:inset-y-0 lg:z-30 lg:flex lg:w-[17.2rem] lg:flex-col bg-zinc-50 lg:shrink-0">
              {sideNav}
            </div>
            <div className="grow bg-white flex flex-col h-screen">
              <Routes>
                <Route
                  index
                  element={<Navigate to="notifications" replace />}
                />
                {allRoutes.map((item) => (
                  <Route
                    key={item.path}
                    path={item.path}
                    element={
                      <>
                        <PageHeader>
                          <PageTitle>{item.name}</PageTitle>
                        </PageHeader>
                        <div className="flex-1 min-h-0 overflow-y-auto">
                          {item.page}
                        </div>
                      </>
                    }
                  />
                ))}
              </Routes>
            </div>
          </div>
          {/* Settings is a focus-trapping modal, so the global toaster (outside it) is inert here.
              Mount one inside the dialog so live toasts still show while in settings. The bell itself
              lives in PageHeader, which renders inside this dialog. */}
          {portalNotificationsEnabled && <NotificationToaster />}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default SettingsTemplate;
